/**
 * taskRuntime Pinia Store
 * 独立管理任务运行态生命周期，不扩展 commandCenter store
 * 通过 session_id 关联任务，管理 SSE 订阅与快照
 */
import { defineStore } from 'pinia'
import type { ActiveTaskInfo, SnapshotState, TaskStreamEvent } from '@/types/task'
import { cancelChatTask } from '@/api/chatbi'
import { getProjectId, tokenHolder } from '@/utils'
import { SSEClient } from '@/transport/sse-client'
import { logger } from '@/utils/logger'

// SSE 连接端点
const TASK_STREAM_BASE = '/api/v1/tasks/stream'

// 任务状态查询端点
const TASK_STATUS_BASE = '/api/v1/tasks'

// 轮询间隔（毫秒）
const POLL_INTERVAL_MS = 5000

// 最大轮询次数（5s × 60 = 5 分钟上限）
const MAX_POLL_COUNT = 60

// 指数退避上限（毫秒）
const MAX_BACKOFF_MS = 16000

// 最大重连次数（超过后放弃重连）
const MAX_RETRY_COUNT = 8

// 浏览器 tab 级稳定 client_id：sessionStorage 关闭 tab 即失效，
// 用于后端按 (task_id, client_id) 去重，消除 HMR/重挂载/多入口的连接堆积。
const CLIENT_INSTANCE_ID_STORAGE_KEY = '__datamanus_sse_client_id__'

function _generateClientInstanceId(): string {
  // lib.dom 已声明 globalThis.crypto，不需要双重 unknown 断言。
  const cryptoApi = globalThis.crypto as Crypto | undefined
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID().replace(/-/g, '').toLowerCase()
  }
  // 降级：32 位 hex 随机串（crypto.getRandomValues 或 Math.random 兜底）
  const bytes = new Uint8Array(16)
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(bytes)
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

export function getClientInstanceId(): string {
  try {
    const cached = sessionStorage.getItem(CLIENT_INSTANCE_ID_STORAGE_KEY)
    if (cached && /^[a-f0-9]{8,64}$/.test(cached)) {
      return cached
    }
    const fresh = _generateClientInstanceId()
    sessionStorage.setItem(CLIENT_INSTANCE_ID_STORAGE_KEY, fresh)
    return fresh
  } catch {
    // sessionStorage 不可用（隐私模式/SSR）：退化为本次调用级别的临时 ID
    return _generateClientInstanceId()
  }
}

// 任务流回调接口
export interface TaskStreamCallbacks {
  onEvent?: (event: TaskStreamEvent) => void
  onError?: (error: Event) => void
  onComplete?: () => void
}

// 内部 SSE 连接信息（基于统一 SSEClient）
interface StreamInfo {
  client: SSEClient
  retryCount: number
  taskId: string
  callbacks: TaskStreamCallbacks
  lastEventId?: string
}

interface PolledTaskStatusResponse {
  status?: string
  result?: Record<string, unknown>
  final_result?: Record<string, unknown>
}

const ACTIVE_TASK_STATUS_SET = new Set<ActiveTaskInfo['status']>([
  'planning',
  'executing',
  'awaiting_approval',
  'awaiting_review',
  'aggregating',
])

function isRecoverableTask(task: ActiveTaskInfo | undefined): task is ActiveTaskInfo {
  return !!task && task.resumable && ACTIVE_TASK_STATUS_SET.has(task.status)
}

/**
 * 兼容轮询接口的终态字段差异。
 * 后端 TaskEngine 当前返回 `complete`，历史实现和部分文档中可能写成 `completed`。
 */
function normalizePolledTaskStatus(status: string): ActiveTaskInfo['status'] | 'pending' {
  if (status === 'completed') {
    return 'complete'
  }
  return status as ActiveTaskInfo['status'] | 'pending'
}

/**
 * 兼容轮询接口最终结果字段差异。
 * `/tasks/{id}` 当前返回 `final_result`，保留对旧 `result` 字段的兼容。
 */
function extractPolledFinalResult(data: PolledTaskStatusResponse): Record<string, unknown> | undefined {
  return data.final_result ?? data.result
}

interface ProgressEventPayload {
  progress_percent?: unknown
  estimated_duration_s?: unknown
  remaining_duration_s?: unknown
  pending_subtasks_count?: unknown
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null
  }
  return value
}

function toBoundedPercent(value: unknown): number | undefined {
  const numeric = toFiniteNumber(value)
  if (numeric === null) {
    return undefined
  }
  return Math.round(Math.min(100, Math.max(0, numeric)))
}

function toNullableRoundedSeconds(value: unknown): number | null | undefined {
  if (value === null) {
    return null
  }
  const numeric = toFiniteNumber(value)
  if (numeric === null || numeric < 0) {
    return undefined
  }
  return Math.round(numeric)
}

function toNullableCount(value: unknown): number | null | undefined {
  if (value === null) {
    return null
  }
  const numeric = toFiniteNumber(value)
  if (numeric === null || numeric < 0) {
    return undefined
  }
  return Math.floor(numeric)
}

function extractProgressSummary(event: TaskStreamEvent & ProgressEventPayload): Partial<ActiveTaskInfo> | null {
  const progressPercent = toBoundedPercent(event.progress_percent)
  const estimatedDurationS = toNullableRoundedSeconds(event.estimated_duration_s)
  const remainingDurationS = toNullableRoundedSeconds(event.remaining_duration_s)
  const pendingSubtasksCount = toNullableCount(event.pending_subtasks_count)
  const summary: Partial<ActiveTaskInfo> = {}

  if (progressPercent !== undefined) {
    summary.progressPercent = progressPercent
  }
  if (estimatedDurationS !== undefined) {
    summary.estimatedDurationS = estimatedDurationS
  }
  if (remainingDurationS !== undefined) {
    summary.remainingDurationS = remainingDurationS
  }
  if (pendingSubtasksCount !== undefined) {
    summary.pendingSubtasksCount = pendingSubtasksCount
  }

  return Object.keys(summary).length > 0 ? summary : null
}

export const useTaskRuntimeStore = defineStore('taskRuntime', {
  state: () => ({
    // session_id → ActiveTaskInfo 映射
    tasksBySession: new Map<string, ActiveTaskInfo>(),
    // task_id → ActiveTaskInfo 映射；任务中心和跨场景恢复以 task_id 为主索引
    tasksById: new Map<string, ActiveTaskInfo>(),
    // task_id → StreamInfo 映射
    streamsByTask: new Map<string, StreamInfo>(),
    // task_id → 最近收到的 event_id（用于去重）
    lastEventIds: new Map<string, string>(),
    // 已处理的 event_id 集合（用于去重判断）
    processedEventIds: new Map<string, Set<string>>(),
    // session_id → SnapshotState 映射
    snapshots: new Map<string, SnapshotState>(),
    // 已收到终止事件（complete/failed/cancelled）的 task_id 集合
    terminalSeen: new Set<string>(),
    // task_id → 运行态异常提示（任务丢失、无权访问、轮询超时等）
    runtimeIssuesByTask: new Map<string, string>(),
    // task_id → pending 重连定时器；用于保证每 task 至多一个待执行重连，
    // 并在 detach 时取消，避免离开任务后定时器仍触发出"无主"并发连接。
    reconnectTimers: new Map<string, ReturnType<typeof setTimeout>>(),
  }),

  getters: {
    /**
     * 是否有活动任务
     */
    hasActiveTask: (state) => (sessionId: string): boolean => {
      return isRecoverableTask(state.tasksBySession.get(sessionId))
    },

    /**
     * 任务是否正在运行中（planning/executing/aggregating）
     */
    isRunning: (state) => (sessionId: string): boolean => {
      return isRecoverableTask(state.tasksBySession.get(sessionId))
    },
  },

  actions: {
    /**
     * 注册任务到 session 映射
     */
    registerTask(sessionId: string, taskInfo: ActiveTaskInfo): void {
      this.tasksBySession.set(sessionId, { ...taskInfo })
      this.tasksById.set(taskInfo.task_id, { ...taskInfo })
    },

    /**
     * 以 task_id 为主索引注册任务，可选同步 session 投影。
     */
    registerTaskById(taskInfo: ActiveTaskInfo, sessionId?: string): void {
      this.tasksById.set(taskInfo.task_id, { ...taskInfo })
      if (sessionId) {
        this.tasksBySession.set(sessionId, { ...taskInfo })
      }
    },

    /**
     * 获取 session 对应的活动任务
     */
    getActiveTask(sessionId: string): ActiveTaskInfo | undefined {
      return this.tasksBySession.get(sessionId)
    },

    /**
     * 获取 task_id 对应的任务运行态。
     */
    getTaskById(taskId: string): ActiveTaskInfo | undefined {
      return this.tasksById.get(taskId)
    },

    /**
     * 获取任务运行态异常提示，用于任务中心展示丢失/无权访问等恢复失败原因。
     */
    getTaskRuntimeIssue(taskId: string): string | undefined {
      return this.runtimeIssuesByTask.get(taskId)
    },

    /**
     * 清除任务映射 + 关闭 SSE + 清理 snapshot
     */
    removeTask(sessionId: string): void {
      const task = this.tasksBySession.get(sessionId)
      if (task) {
        // 关闭 SSE 连接
        this.detachStream(task.task_id)
        // 清理去重集合
        this.processedEventIds.delete(task.task_id)
        this.lastEventIds.delete(task.task_id)
        this.tasksById.delete(task.task_id)
        this.runtimeIssuesByTask.delete(task.task_id)
      }
      this.tasksBySession.delete(sessionId)
      this.snapshots.delete(sessionId)
    },

    /**
     * 仅清理 session 到任务的映射，保留渲染快照供历史回放恢复。
     */
    clearTaskMapping(sessionId: string): void {
      this.tasksBySession.delete(sessionId)
    },

    /**
     * 根据 task_id 查找所属 session_id。
     */
    findSessionIdByTaskId(taskId: string): string | null {
      for (const [sessionId, task] of this.tasksBySession.entries()) {
        if (task.task_id === taskId) {
          return sessionId
        }
      }
      return null
    },

    /**
     * 更新任务运行状态，避免完成后的残留映射继续被当成活动任务。
     */
    updateTaskStatus(taskId: string, status: ActiveTaskInfo['status']): void {
      const canCancel = ACTIVE_TASK_STATUS_SET.has(status)
      const sessionId = this.findSessionIdByTaskId(taskId)
      const taskById = this.tasksById.get(taskId)
      const sessionTask = sessionId ? this.tasksBySession.get(sessionId) : undefined
      const baseTask = taskById ?? sessionTask

      if (!baseTask) {
        return
      }

      const nextTask = {
        ...baseTask,
        status,
        can_cancel: canCancel,
      }
      this.runtimeIssuesByTask.delete(taskId)
      this.tasksById.set(taskId, nextTask)

      if (sessionId) {
        this.tasksBySession.set(sessionId, nextTask)
      }
    },

    /**
     * 更新任务到已取消状态，保持 task_id 主索引和 session 投影一致。
     */
    markTaskCancelled(taskId: string): void {
      this.updateTaskStatus(taskId, 'cancelled')
    },

    /**
     * 更新任务进度 ETA 字段，兼容缺字段的旧后端事件。
     */
    updateTaskProgress(taskId: string, event: TaskStreamEvent & ProgressEventPayload): void {
      const summary = extractProgressSummary(event)
      if (!summary) {
        return
      }

      const sessionId = this.findSessionIdByTaskId(taskId)
      const taskById = this.tasksById.get(taskId)
      const sessionTask = sessionId ? this.tasksBySession.get(sessionId) : undefined
      const baseTask = taskById ?? sessionTask

      if (!baseTask) {
        return
      }

      const nextTask = {
        ...baseTask,
        ...summary,
      }
      this.tasksById.set(taskId, nextTask)
      if (sessionId) {
        this.tasksBySession.set(sessionId, nextTask)
      }
    },

    /**
     * 建立 SSE 连接（基于统一 SSEClient，通过 Authorization header 传递 token）
     * 支持指数退避重连。
     */
    attachStream(
      taskId: string,
      callbacks: TaskStreamCallbacks,
      lastEventId?: string,
      projectId?: string
    ): void {
      // 如已有连接，先关闭
      this.detachStream(taskId)

      // 构建 URL：仅携带 project_id / last_event_id / client_id，token 走 header
      const params = new URLSearchParams()
      const effectiveProjectId = projectId || getProjectId()
      if (effectiveProjectId) {
        params.set('project_id', effectiveProjectId)
      }
      if (lastEventId) {
        params.set('last_event_id', lastEventId)
      }
      // tab 级 client_id：后端按 (task_id, client_id) 去重，
      // 同 tab 的重连/重挂载会替换老订阅而不是堆积。
      params.set('client_id', getClientInstanceId())
      const queryString = params.toString()
      const url = `${TASK_STREAM_BASE}/${taskId}${queryString ? `?${queryString}` : ''}`

      // 初始化去重集合
      if (!this.processedEventIds.has(taskId)) {
        this.processedEventIds.set(taskId, new Set<string>())
      }

      // 使用 SSEClient 保留自定义重连策略（不使用 SSEClient 内置重连）
      const client = new SSEClient({
        url,
        method: 'GET',
        maxRetries: 0, // 由本 store 的 _scheduleReconnect 管理重连
        getToken: () => tokenHolder.hasAccessToken() ? tokenHolder.getAccessToken() : null,
        onMessage: (event) => {
          const msgEvent = new MessageEvent('message', { data: event.data })
          this._handleStreamMessage(taskId, msgEvent)
        },
        onError: () => {
          this._scheduleReconnect(taskId, callbacks)
        },
        onClose: () => {
          // 流正常结束，尝试重连
          const info = this.streamsByTask.get(taskId)
          if (info) {
            this._scheduleReconnect(taskId, callbacks)
          }
        },
      })

      const streamInfo: StreamInfo = {
        client,
        retryCount: 0,
        taskId,
        callbacks,
        lastEventId,
      }
      this.streamsByTask.set(taskId, streamInfo)

      void client.connect()
    },

    /**
     * 关闭 SSE 连接（不清除任务映射，用于切换页面场景）
     */
    detachStream(taskId: string): void {
      // 先取消 pending 重连定时器：否则离开任务后定时器仍会触发 attachStream，
      // 建出一条 store 追踪不到的"僵尸"连接，与同 client_id 的其他连接互踢。
      this._cancelReconnect(taskId)
      const info = this.streamsByTask.get(taskId)
      if (info) {
        this.streamsByTask.delete(taskId)
        info.client.disconnect()
      }
    },

    /** 取消并清除某 task 的 pending 重连定时器（幂等） */
    _cancelReconnect(taskId: string): void {
      const timerId = this.reconnectTimers.get(taskId)
      if (timerId !== undefined) {
        clearTimeout(timerId)
        this.reconnectTimers.delete(taskId)
      }
    },

    /**
     * 取消任务：调用 cancelChatTask API + 更新本地状态
     */
    async cancelTask(sessionId: string): Promise<void> {
      const task = this.tasksBySession.get(sessionId)
      if (!task) {
        logger.warn(`[taskRuntime] 取消任务失败：session ${sessionId} 无活动任务`)
        return
      }

      try {
        await cancelChatTask(sessionId, task.task_id)

        this.markTaskCancelled(task.task_id)
      } catch (error) {
        logger.warn('[taskRuntime] 取消任务请求失败，降级为本地状态更新:', error)
        this.markTaskCancelled(task.task_id)
      }
    },

    /**
     * 按 task_id 取消任务：走 TaskEngine 通用取消 API。
     */
    async cancelTaskById(taskId: string): Promise<void> {
      const task = this.tasksById.get(taskId)
      const sessionId = this.findSessionIdByTaskId(taskId)
      const sessionTask = sessionId ? this.tasksBySession.get(sessionId) : undefined
      if (!task && !sessionTask) {
        logger.warn(`[taskRuntime] 取消任务失败：task ${taskId} 不存在`)
        return
      }

      const headers: Record<string, string> = {}
      if (tokenHolder.hasAccessToken()) {
        headers.Authorization = `Bearer ${tokenHolder.getAccessToken()}`
      }

      try {
        const response = await fetch(`${TASK_STATUS_BASE}/${taskId}/cancel`, {
          method: 'POST',
          headers,
        })
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        this.markTaskCancelled(taskId)
      } catch (error) {
        logger.warn('[taskRuntime] 通用取消任务请求失败，降级为本地状态更新:', error)
        this.markTaskCancelled(taskId)
      }
    },

    /**
     * 从会话列表的 active_task 字段批量恢复任务
     * 仅恢复 resumable=true 且有 active_task 的会话
     */
    hydrateFromSessions(
      sessions: Array<{ session_id: string; active_task?: ActiveTaskInfo | null }>
    ): void {
      for (const session of sessions) {
        const activeTask = session.active_task ?? undefined
        if (isRecoverableTask(activeTask)) {
          this.registerTask(session.session_id, activeTask)
        } else {
          this.clearTaskMapping(session.session_id)
        }
      }
    },

    /**
     * 保存渲染快照（partial/final 标记）
     */
    saveSnapshot(
      sessionId: string,
      data: unknown,
      status: 'partial' | 'final'
    ): void {
      this.snapshots.set(sessionId, {
        data,
        status,
        timestamp: Date.now(),
      })
    },

    /**
     * 获取快照
     * @param ignorePartial 是否忽略 partial 快照（恢复时传 true）
     */
    getSnapshot(
      sessionId: string,
      ignorePartial = false
    ): SnapshotState | null {
      const snap = this.snapshots.get(sessionId)
      if (!snap) return null
      if (ignorePartial && snap.status === 'partial') return null
      return snap
    },

    /**
     * 处理 SSE 消息，含事件去重逻辑
     */
    _handleStreamMessage(taskId: string, event: MessageEvent): void {
      const info = this.streamsByTask.get(taskId)
      if (!info) return

      let parsed: TaskStreamEvent & { event_id?: string }
      try {
        const raw = JSON.parse(event.data as string)
        // broadcaster 将业务字段包裹在 data 信封中，展平到顶层以匹配前端类型定义
        // 例如 { type, task_id, data: { payload, status, result } } → { type, task_id, payload, status, result }
        const { data: envelope, ...rest } = raw
        parsed = (envelope && typeof envelope === 'object' && !Array.isArray(envelope))
          ? { ...rest, ...envelope } as TaskStreamEvent & { event_id?: string }
          : raw as TaskStreamEvent & { event_id?: string }
      } catch {
        logger.warn('[taskRuntime] SSE JSON 解析失败:', event.data)
        return
      }

      // 事件去重：相同 event_id 不重复处理
      if (parsed.event_id) {
        const processed = this.processedEventIds.get(taskId)
        if (processed) {
          if (processed.has(parsed.event_id)) {
            return // 跳过重复事件
          }
          processed.add(parsed.event_id)
        }
        // 更新 lastEventId
        this.lastEventIds.set(taskId, parsed.event_id)
        info.lastEventId = parsed.event_id
      }

      // 重置退避计数（成功收到消息）
      info.retryCount = 0

      if (parsed.type === 'status_change') {
        this.updateTaskStatus(taskId, parsed.status)
      }

      this.updateTaskProgress(taskId, parsed as TaskStreamEvent & ProgressEventPayload)

      if (parsed.type === 'complete') {
        this.terminalSeen.add(taskId)
        this.updateTaskStatus(taskId, parsed.status)
        const sessionId = this.findSessionIdByTaskId(taskId)
        if (sessionId) {
          this.clearTaskMapping(sessionId)
        }
      }

      if (parsed.type === 'failed') {
        this.terminalSeen.add(taskId)
      }

      if (parsed.type === 'cancelled') {
        this.terminalSeen.add(taskId)
        this.updateTaskStatus(taskId, 'cancelled')
        const sessionId = this.findSessionIdByTaskId(taskId)
        if (sessionId) {
          this.clearTaskMapping(sessionId)
        }
      }

      // 分发事件
      info.callbacks.onEvent?.(parsed)

      // done 事件 → 正常收尾；cancelled → 直接关闭
      if (parsed.type === 'done' || parsed.type === 'cancelled') {
        if (parsed.type === 'done' && !this.terminalSeen.has(taskId)) {
          // SSE 流结束但未收到终止事件（subscriber 空闲超时等场景）
          // 降级为 HTTP 轮询获取最终结果，而非直接显示空结果
          console.info(`[taskRuntime] SSE done 但未收到终止事件，降级为轮询: ${taskId}`)
          this.streamsByTask.delete(taskId)
          info.client.disconnect()
          this._pollTaskStatus(taskId, info.callbacks)
          return
        }
        const sessionId = this.findSessionIdByTaskId(taskId)
        if (sessionId) {
          this.clearTaskMapping(sessionId)
        }
        info.callbacks.onComplete?.()
        this.detachStream(taskId)
        this.processedEventIds.delete(taskId)
        this.lastEventIds.delete(taskId)
        this.terminalSeen.delete(taskId)
      }
    },

    /**
     * 指数退避重连
     */
    _scheduleReconnect(taskId: string, callbacks: TaskStreamCallbacks): void {
      const info = this.streamsByTask.get(taskId)
      if (!info) return

      // 去重：若已有 pending 重连定时器，先取消，保证每 task 至多一条重连在途，
      // 避免多个定时器并发触发 attachStream 互相覆盖、堆出并发连接。
      this._cancelReconnect(taskId)

      const currentRetry = info.retryCount

      // 超过最大重连次数，通知上层并降级为 HTTP 轮询获取任务最终状态
      if (currentRetry >= MAX_RETRY_COUNT) {
        this.streamsByTask.delete(taskId)
        info.client.disconnect()
        console.info(`[taskRuntime] SSE 重连 ${MAX_RETRY_COUNT} 次失败，降级为 HTTP 轮询: ${taskId}`)
        // 通知调用方已达最大重试次数
        callbacks.onError?.(new Event('max-retry-exceeded'))
        this._pollTaskStatus(taskId, callbacks)
        return
      }

      // 关闭已有连接
      this.streamsByTask.delete(taskId)
      info.client.disconnect()

      // 计算退避时间：1s, 2s, 4s, 8s, 16s（上限）
      const backoffMs = Math.min(1000 * Math.pow(2, currentRetry), MAX_BACKOFF_MS)

      const timerId = setTimeout(() => {
        this.reconnectTimers.delete(taskId)
        // 重连时携带 lastEventId
        const lastEventId = this.lastEventIds.get(taskId) || info.lastEventId
        this.attachStream(taskId, callbacks, lastEventId)

        // 更新重试计数（从旧 info 正确携带到新 info）
        const newInfo = this.streamsByTask.get(taskId)
        if (newInfo) {
          newInfo.retryCount = currentRetry + 1
        }
      }, backoffMs)
      this.reconnectTimers.set(taskId, timerId)
    },

    /**
     * SSE 重连失败后降级为 HTTP 轮询，获取任务最终状态
     * 轮询直到任务完成/失败/取消，或达到上限
     */
    _pollTaskStatus(taskId: string, callbacks: TaskStreamCallbacks): void {
      let pollCount = 0

      const poll = async () => {
        pollCount++
        if (pollCount > MAX_POLL_COUNT) {
          logger.warn(`[taskRuntime] 轮询超过 ${MAX_POLL_COUNT} 次，放弃: ${taskId}`)
          callbacks.onError?.(new Event('poll-timeout'))
          return
        }

        try {
          const headers: Record<string, string> = {}
          if (tokenHolder.hasAccessToken()) {
            headers['Authorization'] = `Bearer ${tokenHolder.getAccessToken()}`
          }
          const projectId = getProjectId()
          const response = await fetch(
            `${TASK_STATUS_BASE}/${taskId}?project_id=${projectId}`,
            { headers }
          )

          if (!response.ok) {
            if (response.status === 403 || response.status === 404) {
              const issue = response.status === 403 ? '无权访问该任务' : '任务不存在或无权访问'
              this.runtimeIssuesByTask.set(taskId, issue)
              callbacks.onError?.(new Event('task-lost'))
              const sessionId = this.findSessionIdByTaskId(taskId)
              if (sessionId) {
                this.clearTaskMapping(sessionId)
              }
              return
            }
            logger.warn(`[taskRuntime] 轮询任务状态失败: HTTP ${response.status}`)
            setTimeout(poll, POLL_INTERVAL_MS)
            return
          }

          const data = await response.json() as PolledTaskStatusResponse
          const status = normalizePolledTaskStatus(String(data.status || ''))

          // 任务仍在执行中，继续轮询
          if (['pending', 'planning', 'executing', 'aggregating'].includes(status)) {
            this.updateTaskStatus(taskId, status as ActiveTaskInfo['status'])
            setTimeout(poll, POLL_INTERVAL_MS)
            return
          }

          // 任务已终结（complete/failed/cancelled）
          console.info(`[taskRuntime] 轮询获取任务最终状态: ${status}`)
          this.updateTaskStatus(taskId, status as ActiveTaskInfo['status'])

          // 如果有最终结果，构造 complete 事件分发给回调
          const finalResult = extractPolledFinalResult(data)
          if (status === 'complete' && finalResult) {
            callbacks.onEvent?.({
              type: 'complete',
              task_id: taskId,
              status: 'complete',
              result: finalResult,
            } as TaskStreamEvent)
          }

          // 清理映射
          const sessionId = this.findSessionIdByTaskId(taskId)
          if (sessionId) {
            this.clearTaskMapping(sessionId)
          }
          callbacks.onComplete?.()

        } catch (err) {
          logger.warn('[taskRuntime] 轮询请求异常:', err)
          setTimeout(poll, POLL_INTERVAL_MS)
        }
      }

      // 首次轮询延迟 2 秒（给后端一点时间处理）
      setTimeout(poll, 2000)
    },
  },
})
